import { z } from 'zod';
import { schemaDebug } from '../../../utils/schemaLogger';
import { EmvDataSchema, ReceiptDataSchema, ReceiptObjectDataSchema } from '../common/Receipt';

// Main capture response schema
const CaptureResponseSchema = z.object({
  // Core transaction fields
  merchid: z.string()
    .max(16, { message: "Merchant ID cannot exceed 16 characters" })
    .optional(),
  token: z.string()
    .max(19, { message: "Token cannot exceed 19 characters" })
    .optional(),
  respstat: z.enum(['A', 'B', 'C'], {
    message: "Response status must be A (Approved), B (Retry), or C (Declined)"
  })
    .optional(),
  respcode: z.string()
    .max(3, { message: "Response code cannot exceed 3 characters" })
    .optional(),
  resptext: z.string()
    .max(40, { message: "Response text cannot exceed 40 characters" })
    .optional(),
  respproc: z.string()
    .max(4, { message: "Response processor cannot exceed 4 characters" })
    .optional(),
  amount: z.string()
    .max(12, { message: "Amount cannot exceed 12 characters" })
    .optional(),
  authcode: z.union([
    z.string().max(6, { message: "Auth code cannot exceed 6 characters" }),
    z.number().int().positive()
  ])
    .optional(),
  account: z.string()
    .max(27, { message: "Account cannot exceed 27 characters" })
    .optional(),

  // Profile fields
  profileid: z.string()
    .max(20, { message: "Profile ID cannot exceed 20 characters" })
    .optional(),
  acctid: z.string()
    .max(3, { message: "Account ID cannot exceed 3 characters" })
    .optional(),
  defaultacct: z.enum(['Y', 'N'], {
    message: "Default account must be Y or N"
  })
    .optional(),

  // Transaction reference fields
  orderid: z.string()
    .max(50, { message: "Order ID cannot exceed 50 characters" })
    .optional(),
  retref: z.string()
    .max(32, { message: "Retrieval reference cannot exceed 32 characters" })
    .optional(),
  batchid: z.string()
    .max(12, { message: "Batch ID cannot exceed 12 characters" })
    .optional(),

  // Card and settlement info
  commcard: z.enum(['Y', 'N'], {
    message: "Commercial card must be Y or N"
  })
    .optional(),
  setlstat: z.enum([
    'Authorized',
    'Declined',
    'Queued for Capture',
    'Voided',
    'Zero Amount'
  ], {
    message: "Settlement status must be Authorized, Declined, Queued for Capture, Voided, or Zero Amount"
  })
    .optional(),

  // Fee information
  fee_authcode: z.string()
    .max(6, { message: "Fee auth code cannot exceed 6 characters" })
    .optional(),
  fee_format: z.enum(['flat', 'percent'], {
    message: "Fee format must be flat or percent"
  })
    .optional(),
  fee_merchid: z.string()
    .max(16, { message: "Fee merchant ID cannot exceed 16 characters" })
    .optional(),
  fee_retref: z.string()
    .optional(), // Can be empty string
  fee_type: z.enum([
    'CVNFEE',
    'SURCHRG',
    'SRVCFEE',
    'SRVC_WAIVED',
    'CVN_WAIVED',
    'SURCHRG_WAIVED',
    'FEE_TXN'
  ], {
    message: "Fee type must be a valid fee type code"
  })
    .optional(),
  fee_value: z.string()
    .optional(),

  emvTagData: z.union([
    z.string().transform((str): z.infer<typeof EmvDataSchema> | undefined => {
      // The wire may carry emvTagData as a JSON string - parse it
      if (!str || str.trim() === '') return undefined;
      try {
        const parsed = JSON.parse(str);
        const validated = EmvDataSchema.safeParse(parsed);
        return validated.success ? validated.data : parsed as z.infer<typeof EmvDataSchema>;
      } catch {
        schemaDebug('Failed to parse emvTagData JSON, returning undefined');
        return undefined;
      }
    }),
    EmvDataSchema, // Already an object (from DB or already parsed)
    z.undefined()
  ]).optional(),

  // Receipt data (can be JSON string from API or already-parsed object from DB)
  receipt: z.union([
    z.string().transform((str): z.infer<typeof ReceiptDataSchema> | undefined => {
      // The wire may carry receipt as a JSON string - parse it
      if (!str || str.trim() === '') return undefined;
      try {
        const parsed = JSON.parse(str);
        const validated = ReceiptDataSchema.safeParse(parsed);
        return validated.success ? validated.data : parsed as z.infer<typeof ReceiptDataSchema>;
      } catch {
        schemaDebug('Failed to parse receipt JSON, returning undefined');
        return undefined;
      }
    }),
    ReceiptDataSchema, // Already an object (from DB or already parsed)
    z.undefined()
  ]).optional(),
  receiptObj: z.any().transform((value): z.infer<typeof ReceiptObjectDataSchema> | undefined => {
    // Handle undefined/null
    if (value === undefined || value === null) {
      return undefined;
    }

    // If it's already an object, validate and return it
    if (typeof value === 'object') {
      const validated = ReceiptObjectDataSchema.safeParse(value);
      return validated.success ? validated.data : value as z.infer<typeof ReceiptObjectDataSchema>;
    }

    // If it's a string, parse it as JSON
    if (typeof value === 'string') {
      if (value.trim() === '') return undefined;
      try {
        const parsed = JSON.parse(value);
        const validated = ReceiptObjectDataSchema.safeParse(parsed);
        return validated.success ? validated.data : parsed as z.infer<typeof ReceiptObjectDataSchema>;
      } catch {
        schemaDebug('Failed to parse receiptObj JSON, returning undefined');
        return undefined;
      }
    }

    // Otherwise return undefined
    return undefined;
  }).optional(),
}).strict()
  .refine((data) => {
    // Validation: If profile is present, it should have a valid format
    if (data.profileid && data.profileid.length > 0) {
      // ProfileID should be numeric string of appropriate length
      if (!/^\d+$/.test(data.profileid)) {
        return false;
      }
    }
    return true;
  }, {
    message: "Profile ID must be a numeric string",
    path: ["profileid"]
  })
  .refine((data) => {
    // Validation: If account ID is present, it should be numeric
    if (data.acctid && data.acctid.length > 0) {
      if (!/^\d+$/.test(data.acctid)) {
        return false;
      }
    }
    return true;
  }, {
    message: "Account ID must be a numeric string",
    path: ["acctid"]
  })
  .refine((data) => {
    // Validation: Fee fields consistency
    if (data.fee_type && !data.fee_format && !data.fee_value) {
      // If fee_type is present, usually fee_format or fee_value should also be present
      // But this might be too strict, so we'll allow it for now
    }
    return true;
  }, {
    message: "Fee information should be consistent",
    path: ["fee_type"]
  });

// Schema for approved responses
const ApprovedCaptureResponseSchema = CaptureResponseSchema.safeExtend({
  respstat: z.literal('A'),
  setlstat: z.enum(['Authorized', 'Queued for Capture'], {
    message: "Approved captures should be Authorized or Queued for Capture"
  }).optional(),
});

// Schema for declined responses
const DeclinedCaptureResponseSchema = CaptureResponseSchema.safeExtend({
  respstat: z.literal('C'),
  respcode: z.string()
    .min(1, { message: "Response code is required for declined transactions" })
    .max(3, { message: "Response code cannot exceed 3 characters" }),
  resptext: z.string()
    .min(1, { message: "Response text is required for declined transactions" })
    .max(40, { message: "Response text cannot exceed 40 characters" }),
  setlstat: z.literal('Declined').optional(),
});

// Schema for retry responses
const RetryCaptureResponseSchema = CaptureResponseSchema.safeExtend({
  respstat: z.literal('B'),
});

// Schema for responses with fees
const CaptureResponseWithFeeSchema = CaptureResponseSchema.safeExtend({
  fee_type: z.enum([
    'CVNFEE',
    'SURCHRG',
    'SRVCFEE',
    'SRVC_WAIVED',
    'CVN_WAIVED',
    'SURCHRG_WAIVED',
    'FEE_TXN'
  ]),
  fee_format: z.enum(['flat', 'percent']).optional(),
  fee_value: z.string().optional(),
});

// Schema for responses with profiles
const CaptureResponseWithProfileSchema = CaptureResponseSchema.safeExtend({
  profileid: z.string()
    .min(1, { message: "Profile ID is required when profile is present" })
    .max(20, { message: "Profile ID cannot exceed 20 characters" })
    .regex(/^\d+$/, { message: "Profile ID must be numeric" }),
  acctid: z.string()
    .max(3, { message: "Account ID cannot exceed 3 characters" })
    .regex(/^\d+$/, { message: "Account ID must be numeric" })
    .optional(),
  defaultacct: z.enum(['Y', 'N']).optional(),
});

// Export type definitions
export type CaptureResponseType = z.infer<typeof CaptureResponseSchema>;
export type ApprovedCaptureResponseType = z.infer<typeof ApprovedCaptureResponseSchema>;
export type DeclinedCaptureResponseType = z.infer<typeof DeclinedCaptureResponseSchema>;
export type RetryCaptureResponseType = z.infer<typeof RetryCaptureResponseSchema>;
export type CaptureResponseWithFeeType = z.infer<typeof CaptureResponseWithFeeSchema>;
export type CaptureResponseWithProfileType = z.infer<typeof CaptureResponseWithProfileSchema>;

export default CaptureResponseSchema;
export {
  ApprovedCaptureResponseSchema, CaptureResponseWithFeeSchema,
  CaptureResponseWithProfileSchema, DeclinedCaptureResponseSchema,
  RetryCaptureResponseSchema
};

